<template>
  <div class="campaign-card-wrapper">
    <b-modal
      ref="newCampaignModal"
      title="Create campaign report"
      @show="fillData"
      hide-footer
      no-close-on-backdrop
    >
      <template slot="modal-header">
        <h3 v-if="!campaignData.id">Create new campaign</h3>
        <h3 v-else>Edit campaign <u>#{{ campaignData.id }}</u></h3>
        <div class="actions-holder">
          <b-button @click="copyCampaignConfirm" variant="light" v-if="campaignData.id"><i class="icon-duplicate"></i></b-button>
          <b-button @click="deleteCampaignConfirm" variant="light" v-if="campaignData.id"><i class="icon-delete"></i></b-button>
          <b-button @click="addCampaign" variant="light"><i class="icon-disquette"></i></b-button>
          <b-button @click="closeModal" variant="light" class="btn-close"><i class="icon-close"></i></b-button>
        </div>
      </template>

      <div class="modal-content pb-40">
        <div class="mb-3">
          <label for="client_input">Choose a client</label>
          <div class="client-box">
            <div class="input-holder">
              <v-select id="client_input"
                        ref="client_input"
                        v-model="campaignData.client_id"
                        label="name"
                        :reduce="e => e.id"
                        :value="selected_client_id"
                        :options="allClients"
                        class="triangle-selector"
                        :components="{Deselect}"

              >
                <template slot="search" slot-scope="scope">
                  <input class="vs__search"
                         v-bind="scope.attributes"
                         v-on="scope.events"
                         @keydown="selectValueInput($event,scope,'clients','client_input')"
                  >
                  <div class="select-actions">
                    <a href="#" @click="editReferenceName('clients',campaignData.client_id)" class="vs__open_link"><i
                      class="icon-edit-pencil"></i></a>
                  </div>
                </template>
              </v-select>
            </div>
            <div v-if="clientsEditAccess" class="d-flex align-items-center">
              <span class="px-3">or</span>
              <b-button @click="createClient" @saved="afterClientAdding" variant="light" class="px-5">Create a new one
              </b-button>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <b-row>
            <b-col md="6">
              <label for="campaignTitle">Campaign title</label>
              <div class="input-holder">
                <b-input v-model='campaignData.title' id="campaignTitle" name="campaignTitle"
                         placeholder="The title of the campaign"></b-input>
              </div>
            </b-col>
            <b-col md="6">
              <label for="website">Website</label>
              <div class="input-holder">
                <v-select id="website"
                          ref="website"
                          :value="selected_website"
                          label="name"
                          :options="allSites"
                          @input="changeSelectedWebsite"
                          class="triangle-selector position-top"
                          :filter="searchSite"
                          :components="{Deselect}"
                          placeholder="Website of the Client"

                >
                  <template v-slot:option="option">
                    <template v-if="searchSiteText">
                      <span v-html="colorSelection(option.name)"/>
                    </template>
                    <template v-else>
                      {{ option.name }}
                    </template>
                  </template>
                </v-select>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-container class="mb-3 px-0">
          <b-row>
            <b-col md="4">
              <h5>Production days</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.prod_days' @change.native="recalcDates(1)"
                              placeholder="Enter 35, 40 or 45"></b-form-input>
              </div>
            </b-col>
            <b-col md="4">
              <h5>Retainer</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.retainer' placeholder="Retainer #"></b-form-input>
              </div>
            </b-col>
            <b-col md="4">
              <h5>Project</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.project_id' placeholder="Project #"></b-form-input>
              </div>
            </b-col>
          </b-row>
        </b-container>

        <b-container class="mb-3 px-0">

          <h5>Ideation</h5>
          <b-row>
            <b-col md="4">
              <label>Started</label>
              <div class="input-holder">
                <date-input v-model='campaignData.project_start'
                            @change.native="recalcDates(0)"
                            initial-date="'0000-00-00'"
                            ref="project_start"
                            :class="{'input-alert':project_start_alert}"
                ></date-input>
              </div>
            </b-col>
            <b-col md="4">
              <label>Sent to client</label>
              <div class="input-holder">
                <date-input v-model='campaignData.idea_sent'
                            @change.native="recalcDates(1)"
                ></date-input>
              </div>
            </b-col>
            <b-col md="4">
              <label>Signed-off</label>
              <div class="input-holder">
                <date-input v-model='campaignData.idea_signoff'
                            @change.native="recalcDates(1)"></date-input>
              </div>
            </b-col>
          </b-row>
        </b-container>

        <b-container class="mb-3 px-0">
          <h5>Production</h5>
          <b-row>
            <b-col md="4">
              <label>Started</label>
              <date-input v-model='campaignData.prod_start' @change.native="recalcDates(2)"></date-input>
            </b-col>
            <b-col md="4">
              <label>Sent to client</label>
              <date-input v-model='campaignData.prod_sent'></date-input>
            </b-col>
            <b-col md="4">
              <label>Ended</label>
              <date-input v-model='campaignData.prod_end'></date-input>
            </b-col>
          </b-row>
        </b-container>

        <b-container class="mb-3 px-0">
          <b-row>
            <b-col md="4">
              <h5>Promotion</h5>
              <b-row>
                <b-col md="12">
                  <label>Started</label>
                  <div class="input-holder">
                    <date-input v-model='campaignData.promotion_start'></date-input>
                  </div>
                </b-col>
              </b-row>
            </b-col>

            <b-col md="8">
              <h5>Report</h5>
              <b-row>
                <b-col md="6">
                  <label>Find by title</label>
                  <div class="input-holder">
                    <v-select id="report_input"
                              ref="report_input"
                              :value="selected_report"
                              @input="changeSelectedReport"
                              label="id"
                              :options="allReports"
                              class="triangle-selector position-top"
                              :filter="searchReport"
                              :components="{Deselect}"
                              placeholder="Search Campaign"

                    >
                      <template v-slot:option="option">
                        <template v-if="searchText">
                          <span v-html="colorSelection(option.title)"/>
                        </template>
                        <template v-else>
                          {{ option.title }}
                        </template>
                      </template>
                    </v-select>
                  </div>
                </b-col>
                <b-col md="6">
                  <label>Sent</label>
                  <div class="input-holder">
                    <date-input v-model='campaignData.report_sent'></date-input>
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <b-col sm="12" v-if="selected_report" class="mt-2">
              <span>Report title: </span>
              <span>{{ slectedReportTitle }}</span>
            </b-col>
          </b-row>

        </b-container>

        <b-container class="mb-3 px-0">
          <b-row>
            <b-col md="4">
              <h5>Report URL</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.report_url' @change.native="recalcDates(1)"
                              placeholder="Report url"></b-form-input>
              </div>
            </b-col>

            <b-col md="4">
              <h5>Links</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.links_num' placeholder="Number of links"></b-form-input>
              </div>
            </b-col>

            <b-col md="4">
              <h5>Avg.DR</h5>
              <div class="input-holder">
                <b-form-input type="text" v-model='campaignData.manual_avg_dr' placeholder="Average Domain Rating"></b-form-input>
              </div>
            </b-col>
          </b-row>
        </b-container>


      </div>

    </b-modal>

    <client-card ref="newClient" @saved="afterClientAdding"></client-card>

    <b-modal ref="deleteConfirm"
             id="deleteConfirm"
             modal-class="modal-confirm"
             hide-footer
             title="Are you sure you want to delete current project?"
    >
      <div class="d-flex justify-content-around">
        <b-btn @click="deleteCampaign">Yes</b-btn>
        <b-btn @click="$refs.deleteConfirm.hide()">No</b-btn>
      </div>
    </b-modal>
    <b-modal ref="copyConfirm"
             id="copyConfirm"
             modal-class="modal-confirm"
             hide-footer
             title="Are you sure you want to duplicate current project?"
    >
      <div class="d-flex justify-content-around">
        <b-btn @click="copyCampaign">Yes</b-btn>
        <b-btn @click="$refs.copyConfirm.hide()">No</b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '../_helpers/axios';
import {apiUrl} from 'config';
import {createPopper} from '@popperjs/core';
import {store} from "@/_store";

export default {
  name: 'CampaignCard',
  components: {
    'value-item': () => import('@/components/ValueItem.vue'),
    'client-card': () => import('@/components/ClientCard.vue'),
    'date-input': () => import('@/components/DateInput.vue'),
  },
  props: {
    _campaignData: {
      type: Object
    }
  },
  data: () => ({
    campaignData: {
      id: null,
      client_id: null,
      title: '',
    },
    searchText: '',
    searchSiteText: '',
    selected_client_id: null,
    selected_website: null,
    selected_report: null,
    project_start_alert: false,
    Deselect: {
      render: createElement => {
        const el = createElement('i', {
          attrs: {
            class: 'icon-close'
          }
        });
        return el;
      },
    },
  }),
  mounted() {
    this.$store.dispatch('reports/getAll');
  },
  methods: {
    colorSelection(text) {
      var re = new RegExp(this.searchText, 'gi');
      return text?text.replace(re, '<span class="highlighting">$&</span>'):'';
    },
    changeSelectedReport(val) {
      this.selected_report = val;
    },
    changeSelectedWebsite(val) {
      this.selected_website = val;
    },
    withPopper(dropdownList, component, {width}) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width;

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset', options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'bottom')
            },
          }]
      });

      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy();
    },
    searchReport(options, search) {
      this.searchText = search.toLowerCase();
      return options.filter(e => e.title.toLowerCase().indexOf(this.searchText) >= 0);
    },
    searchSite(options, search) {
      this.searchSiteText = search.toLowerCase();
      return options.filter(e => e.website && e.website.toLowerCase().indexOf(this.searchSite) >= 0);
    },
    recalcDates(whatChanged) {
      console.log('recalcDates(whatChanged) ', whatChanged);
      if (!this.$mm()._locale._holidays) {
        this.$store.commit('alert/error', 'Holidays are not defined, dates autocalculation will be disabled, try to reload application page or contact with dev team.');
        return;
      }
      this.$forceUpdate();
      this.$nextTick(() => {
        if (whatChanged === 0) {//Project start
          if (this.project_start) {
            this.campaignData.idea_sent = this.$mm(this.project_start).add(7, 'days').format('YYYY-MM-DD');
            this.recalcDates(1);
          }
        } else if (whatChanged === 1) {//Production days, ideation dates
          if (this.idea_signoff && this.prod_days) {
            this.campaignData.prod_start = this.$mm(this.idea_signoff).businessAdd(1).format('YYYY-MM-DD');
            this.recalcDates(2);
          }
        } else if (whatChanged === 2) {//Production start
          if (this.prod_start) {
            this.campaignData.prod_sent = this.$mm(this.prod_start).businessAdd(parseInt(this.prod_days) + 2).format('YYYY-MM-DD');
          }
        }

        this.$forceUpdate();

      });
    },
    fillData() {
      this.$nextTick(() => {
        if (this._campaignData) {
          this.campaignData = Object.assign({}, this._campaignData);
          if (this.campaignData && this.campaignData.report_id) {
            this.selected_report = this.allReports.find(e => e.id === this.campaignData.report_id);
          }
          // console.log('this._campaignData', this._campaignData);
        }
      });
    },
    afterClientAdding({id}) {
      this.client_id = id;
      this.selected_client_id = id;
      this.campaignData.client_id = id;
    },
    createClient() {
      this.$refs.newClient.show();
    },
    async addCampaign() {
      if (!this.selected_website) {
        store.dispatch('alert/error', 'Website must be selected!', { root: true });
        return
      }
      if (!this.campaignData.project_start) {
        this.project_start_alert = true;
        return;
      }
      // this.campaignData.website_id = this.selected_website.website_id;
      const dataToPost = {
        ...this.campaignData,
        links_num: this.campaignData.links_num || 0,
        manual_avg_dr: this.campaignData.manual_avg_dr || 0,
      }

      await axios.post(`${apiUrl}/api/campaigns/save`, dataToPost);
      this.$emit('done');
      this.$refs.newCampaignModal.hide();
    },
    async deleteCampaignConfirm() {
      this.$refs.deleteConfirm.show();
    },
    async copyCampaignConfirm() {
      this.$refs.copyConfirm.show();
    },
    async deleteCampaign() {
      await axios.post(`${apiUrl}/api/campaigns/delete`, this.campaignData)
        .then(() => {
          this.$refs.newCampaignModal.hide();
          this.$emit('done');
        })
        .finally(() => {
          this.$refs.deleteConfirm.hide();
        });
    },
    async copyCampaign() {
      const copyData = (({ client_id, prod_days, website_id }) => ({ client_id, prod_days, website_id }))(this.campaignData);
      this.campaignData = copyData
      this.$refs.copyConfirm.hide();
    },
    show() {
      this.$refs.newCampaignModal.show();
    },
    closeModal() {
      this.$refs.newCampaignModal.hide();
    },
    selectValueInput(key, scope, refName, refInput) {
      if (key.keyCode === 13) {
        //Enter pressed, we'll check if the value exists
        let slOb = this.$refs[refInput];
        slOb.typeAheadSelect();
      }
    },
  },
  computed: {
    clientsEditAccess() {
      if (!this.$store.state.app.userRights) return false;
      return this.$store.state.app.userRights.clients;
    },
    project_start() {
      return this.campaignData.project_start;
    },
    prod_days: {
      cache: false,
      get() {
        return this.campaignData.prod_days;
      }
    },
    idea_signoff() {
      if (this.campaignData.idea_signoff) {
        return this.campaignData.idea_signoff;
      }
      if (this.campaignData.idea_sent) {
        return this.$mm(this.campaignData.idea_sent).add(7, 'days');
      }
      if (this.project_start) {
        return this.$mm(this.project_start).businessAdd(7);
      }
    },
    prod_start: {
      cache: false,
      get() {
        return this.campaignData.prod_start;
      }
    },
    allClients() {
      let allItems = this.$store.state.refs.clients_active || [];
      return allItems.length ? allItems : [];
    },
    allReports() {
      let allItems = this.$store.state.reports.all || [];
      if (!allItems.length || !this.campaignData) return [];
      // debugger
      allItems = allItems.filter(e => e.client_id === this.campaignData.client_id);
      return allItems.length ? allItems : [];
    },
    allSites() {
      let allItems = this.$store.state.refs.client_sites || [];
      if (!allItems.length || !this.campaignData) return [];
      allItems = allItems
        .filter(e => e.client_id === this.campaignData.client_id);
      return allItems.length ? allItems : [];
    },
    slectedReportTitle() {
      if (!this.selected_report) return '';
      const reports = this.allReports;
      const report = reports.find(e => e.id === this.selected_report.id);
      if (report) {
        return report.title;
      }
    },
  },
  watch: {
    selected_report(val) {
      if (val) {
        this.campaignData.report_id = val.id;
      } else {
        this.campaignData.report_id = null;
      }
    },
    selected_website(val) {
      if (val) {
        this.campaignData.website_id = val.id;
      } else {
        this.campaignData.website_id = null;
      }
    },
    allSites(val) {
      if (val && val.length && this.campaignData) {
        this.selected_website = val.find(e => e.id === this.campaignData.website_id);
      }
    },
  }
};
</script>

<style scoped lang="scss">
@import '../assets/_scss/_component-base';
@import '../assets/_scss/components/input';
@import '../assets/_scss/components/modal';
@import '../assets/_scss/components/v-select';

.modal-header {
  h3 {
    font-size: 18px;
  }

  button.btn {
    @include sm {
      margin-right: 10px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.client-box {
  display: flex;
  align-items: center;

  width: 100%;
  @include sm {
    flex-wrap: wrap;
  }

  .input-holder {
    flex-grow: 1;
    width: 100%;
    @include sm {
      margin-bottom: 15px;
    }
  }

  .btn {
    white-space: nowrap;
  }
}

.actions-holder {
  display: flex;

  > *:not(:last-child) {
    margin-right: 30px;
  }
}

label {
  margin-bottom: 3px;
}

h5 {
  margin-bottom: 3px;
  @include sm {
    margin-top: 15px;
  }

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 2px;
    background: $lgrey5;
    margin-top: 3px;
    border-radius: 3px;
  }
}

.triangle-selector {
  position: relative;

  &.vs--open {
    &:after {
      transform: rotate(180deg);
    }
  }

  &:after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border: 0 solid transparent;
    border-left-width: 10px;
    border-right-width: 10px;
    border-top: 10px solid black;
    position: absolute;
    right: 10px;
    top: 0;
    bottom: 0;
    margin: auto;
    transition: .3s;
  }
}

::v-deep .highlighting {
  background: $lgrey3;
}
</style>
